import { IconInfoCircle } from "@tabler/icons-react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { z } from "zod";
import { PageTabs } from "@/components/patterns/PageTabs";
import { AmazonAuthorizationGuide } from "@/components/settings/AmazonAuthorizationGuide";
import { ConnectAmazonDialog } from "@/components/settings/ConnectAmazonDialog";
import { ManualSyncAction } from "@/components/settings/ManualSyncAction";
import { WorkspaceProfilePanel } from "@/components/settings/workspace-profile-panel";
import { Alert, AlertAction, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { TabsContent } from "@/components/ui/tabs";
import { usePermission } from "@/hooks/use-permission";
import {
  SP_API_QUERY_KEY,
  spApiOAuthErrorKey,
  useSpApiAuthorization,
} from "@/hooks/use-sp-api-authorization";
import { useWorkspaceDataStatus, workspaceDataStatusKey } from "@/hooks/use-workspace-data-status";
import { workspaceSyncQueryKey } from "@/hooks/use-workspace-sync-status";
import {
  deleteShops,
  fetchAmazonAccounts,
  scopeConnectionsToWorkspace,
  setShopActivation,
} from "@/lib/amazon-api";
import { getApiErrorMessage } from "@/lib/api-error";
import { fetchAuthUser, hydrateAuthUser } from "@/lib/auth-api";
import { refreshWorkspaceShopData } from "@/lib/data-status-api";
import { requireModule } from "@/lib/module-access";
import { normalizeSettingsTab, type SettingsTab } from "@/lib/settings-tabs";
import { checkSpApiConnection, fetchSpApiConnections } from "@/lib/sp-api";
import { useAuthStore } from "@/stores/auth";

export const Route = createFileRoute("/_authed/settings")({
  beforeLoad: () => {
    requireModule("settings");
  },
  validateSearch: z.object({
    dataDetails: z.boolean().optional().catch(undefined),
    tab: z
      .enum(["members", "amazon", "amazon-ads", "seller-central", "workspace"])
      .optional()
      .catch(undefined),
    // The SP-API OAuth callback is a browser navigation that ends here; these
    // report its outcome. Unvalidated keys are dropped by the router, so a
    // missing entry would silently discard the seller's only feedback.
    sp_api: z.literal("connected").optional().catch(undefined),
    sp_api_connection: z.string().optional().catch(undefined),
    sp_api_error: z.string().optional().catch(undefined),
  }),
  component: SettingsPage,
});

function SettingsPage(): React.ReactElement {
  const { t } = useTranslation("settings");
  const queryClient = useQueryClient();
  const search = Route.useSearch();
  const [activeTab, setActiveTab] = useState<SettingsTab>(() => normalizeSettingsTab(search.tab));
  const user = useAuthStore((state) => state.user);
  const activeWorkspace = user?.workspaces?.find((item) => item.id === user.activeWorkspaceId);
  const { hasPermission } = usePermission();
  // ADP-167: connection management follows the settings-module grant
  // (main account always; sub-accounts when granted the settings module).
  const canManage = hasPermission("manage_connections");
  const { beginAuthorization, pendingAuthorization, cancelAuthorization } = useSpApiAuthorization();
  const accountsQuery = useQuery({
    queryKey: ["amazon", "accounts"],
    queryFn: fetchAmazonAccounts,
  });
  const spApiConnectionsQuery = useQuery({
    queryKey: SP_API_QUERY_KEY,
    queryFn: fetchSpApiConnections,
  });
  const dataStatusQuery = useWorkspaceDataStatus(activeWorkspace?.id);
  const [isCheckingStatus, setIsCheckingStatus] = useState(false);
  const checkDataStatus = async () => {
    if (isCheckingStatus) return;
    setIsCheckingStatus(true);
    try {
      await dataStatusQuery.refetch();
    } finally {
      setIsCheckingStatus(false);
    }
  };
  const connections = scopeConnectionsToWorkspace(
    accountsQuery.data?.connections ?? [],
    activeWorkspace?.id,
  );
  const [refreshMessage, setRefreshMessage] = useState<string | null>(null);
  const syncShopMutation = useMutation({
    mutationFn: (shopId: string) => refreshWorkspaceShopData(activeWorkspace!.id, shopId),
    onSuccess: (data, shopId) => {
      const results = data.results.flatMap((result) => [
        result.storeInformation,
        result.catalog,
        result.advertising,
        result.sales,
      ]);
      let messageKey = "dataStatus.refreshUnavailable";
      if (results.some((source) => source.status === "failed")) {
        messageKey = "dataStatus.refreshPartial";
      } else if (results.some((source) => source.status === "queued" || source.status === "busy")) {
        messageKey = "dataStatus.refreshAccepted";
      }
      setRefreshMessage(t(messageKey));
      const shop = connections
        .flatMap((connection) => connection.shops)
        .find((item) => item.shopId === shopId);
      void queryClient.invalidateQueries({ queryKey: workspaceDataStatusKey(activeWorkspace?.id) });
      queryClient.invalidateQueries({ queryKey: ["amazon"] });
      if (shop?.workspaceId) {
        queryClient.invalidateQueries({ queryKey: workspaceSyncQueryKey(shop.workspaceId) });
      }
    },
    onError: async (error) => {
      const message = await getApiErrorMessage(error);
      setRefreshMessage(message);
      toast.error(message);
    },
  });
  const shopActivationMutation = useMutation({
    mutationFn: ({ shopId, isActive }: { shopId: string; isActive: boolean }) =>
      setShopActivation(shopId, isActive),
    onSuccess: async (_data, { shopId, isActive }) => {
      const shop = connections
        .flatMap((connection) => connection.shops)
        .find((item) => item.shopId === shopId);
      await queryClient.invalidateQueries({ queryKey: ["amazon"] });
      hydrateAuthUser(await fetchAuthUser());
      toast.success(
        isActive
          ? t("amazonAds.shopReactivated", { name: shop?.shopName ?? "Amazon Shop" })
          : t("amazonAds.shopDeactivated", { name: shop?.shopName ?? "Amazon Shop" }),
      );
    },
    onError: async (error) => toast.error(await getApiErrorMessage(error)),
  });
  const deleteShopsMutation = useMutation({
    mutationFn: ({ shopIds }: { shopIds: string[]; name: string }) => deleteShops(shopIds),
    onSuccess: async (_data, { name }) => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ["amazon"] }),
        queryClient.invalidateQueries({ queryKey: workspaceDataStatusKey(activeWorkspace?.id) }),
      ]);
      // Moves the current Shop off a deleted one, as deactivation does.
      hydrateAuthUser(await fetchAuthUser());
      toast.success(t("amazonAuthorization.delete.deleted", { name }));
    },
    onError: async (error) => toast.error(await getApiErrorMessage(error)),
  });
  const refreshMerchantMutation = useMutation({
    mutationFn: async ({ connectionIds }: { merchantId: string; connectionIds: string[] }) => {
      await Promise.all(connectionIds.map(checkSpApiConnection));
    },
    onError: async (error) => toast.error(await getApiErrorMessage(error)),
    // Keep the button busy until the table shows the re-checked status.
    onSettled: () =>
      Promise.all([
        queryClient.invalidateQueries({ queryKey: ["amazon"] }),
        queryClient.invalidateQueries({ queryKey: workspaceDataStatusKey(activeWorkspace?.id) }),
      ]),
  });

  useEffect(() => {
    if (search.tab) setActiveTab(normalizeSettingsTab(search.tab));
  }, [search.tab]);

  useEffect(() => {
    if (search.dataDetails) setActiveTab("amazon");
  }, [search.dataDetails]);

  useEffect(() => {
    if (!search.dataDetails || activeTab !== "amazon") return;
    const frame = requestAnimationFrame(() => {
      document.getElementById("amazon-authorization-title")?.scrollIntoView({ block: "start" });
    });
    return () => cancelAnimationFrame(frame);
  }, [activeTab, search.dataDetails]);

  const navigate = Route.useNavigate();
  const spApiOutcome = search.sp_api ?? search.sp_api_error;
  useEffect(() => {
    if (!spApiOutcome) return;
    setActiveTab("amazon");
    if (search.sp_api === "connected") {
      toast.success(t("sellerCentral.connected"));
    } else {
      toast.error(
        t(
          search.sp_api_error
            ? spApiOAuthErrorKey(search.sp_api_error)
            : "sellerCentral.oauthErrors.failed",
        ),
      );
    }
    // Clear the parameters so a reload or a shared URL does not replay the
    // toast for an authorization that already finished.
    void navigate({
      to: "/settings",
      search: (previous) => ({
        ...previous,
        sp_api: undefined,
        sp_api_connection: undefined,
        sp_api_error: undefined,
      }),
      replace: true,
    });
  }, [spApiOutcome, search.sp_api, search.sp_api_error, navigate, t]);

  const [connectAmazonOpen, setConnectAmazonOpen] = useState(false);

  return (
    <div className="flex min-h-full w-full min-w-0 flex-1 flex-col gap-[18px] px-6 pb-6 pt-[22px]">
      <header className="h-[52px] shrink-0">
        <h1 className="text-[24px] font-semibold leading-[30px] tracking-[-0.025em] text-ink">
          {t("title")}
        </h1>
        <p className="mt-1 text-[12px] leading-[18px] text-muted-foreground">
          {t("page.subtitle")}
        </p>
      </header>

      <PageTabs
        value={activeTab}
        onValueChange={(value) => {
          if (value === "amazon" || value === "workspace") {
            setActiveTab(value);
          }
        }}
        className="min-h-0 flex-1"
        items={[
          { value: "amazon", label: t("tabs.amazon") },
          { value: "workspace", label: t("tabs.workspace") },
        ]}
      >
        <TabsContent value="amazon" className="min-h-0">
          {pendingAuthorization ? (
            <Alert variant="info" className="mb-4">
              <IconInfoCircle aria-hidden />
              <AlertTitle>{t("sellerCentral.authorizing.title")}</AlertTitle>
              <AlertDescription>{t("sellerCentral.authorizing.description")}</AlertDescription>
              <AlertAction>
                <Button variant="outline" size="sm" onClick={cancelAuthorization}>
                  {t("sellerCentral.authorizing.cancel")}
                </Button>
              </AlertAction>
            </Alert>
          ) : null}
          <AmazonAuthorizationGuide
            key={search.dataDetails ? "details" : "summary"}
            dataStatus={dataStatusQuery.data}
            dataStatusLoading={dataStatusQuery.isLoading}
            dataStatusError={dataStatusQuery.isError}
            onRetryDataStatus={() => void checkDataStatus()}
            onRefreshShop={(shopId) => syncShopMutation.mutate(shopId)}
            refreshingShopId={syncShopMutation.isPending ? syncShopMutation.variables : null}
            refreshMessage={refreshMessage}
            defaultDataDetails={Boolean(search.dataDetails)}
            manualSyncAction={
              activeWorkspace ? (
                <ManualSyncAction
                  key={activeWorkspace.id}
                  workspaceId={activeWorkspace.id}
                  connections={connections}
                  disabled={accountsQuery.isLoading || accountsQuery.isError}
                />
              ) : undefined
            }
            spApiAuthorizing={pendingAuthorization !== null}
            adsConnections={connections}
            adsError={accountsQuery.isError}
            adsLoading={accountsQuery.isLoading}
            canManage={canManage}
            onAuthorizeAds={() => setConnectAmazonOpen(true)}
            onAuthorizeSpApi={(shopId, connectionId) =>
              beginAuthorization(
                connectionId ? { kind: "reauthorize", connectionId } : { kind: "connect", shopId },
              )
            }
            onRetryAds={() => void accountsQuery.refetch()}
            onRetrySpApi={() => void spApiConnectionsQuery.refetch()}
            onRefreshMerchant={(merchantId, connectionIds) =>
              refreshMerchantMutation.mutate({ merchantId, connectionIds })
            }
            refreshingMerchantId={
              refreshMerchantMutation.isPending
                ? (refreshMerchantMutation.variables?.merchantId ?? null)
                : null
            }
            onSetShopActivation={(shopId, isActive) =>
              shopActivationMutation.mutate({ shopId, isActive })
            }
            onDeleteShops={async (shopIds, name) => {
              await deleteShopsMutation.mutateAsync({ shopIds, name });
            }}
            deletingShopIds={
              deleteShopsMutation.isPending ? deleteShopsMutation.variables.shopIds : []
            }
            spApiConnections={spApiConnectionsQuery.data?.connections ?? []}
            spApiError={spApiConnectionsQuery.isError}
            spApiLoading={spApiConnectionsQuery.isLoading}
            changingActivationShopId={
              shopActivationMutation.isPending ? shopActivationMutation.variables.shopId : null
            }
          />
        </TabsContent>

        <TabsContent value="workspace" className="min-h-0">
          <WorkspaceProfilePanel />
        </TabsContent>
      </PageTabs>
      <ConnectAmazonDialog open={connectAmazonOpen} onOpenChange={setConnectAmazonOpen} />
    </div>
  );
}
